import {
  PendingWithholdingSubType,
  WithholdingTransactionType
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  useWSSnackbar,
  WSAmountInput,
  WSButton,
  WSContainer,
  WSForm,
  WSGrid,
  WSIcon,
  WSLayout,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "accounting";
import React from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useBrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../query/hooks/helpers";
import { usePayoutSettings } from "../../query/payments/queries";
import { useCreatePendingWithholding } from "../../query/taxes/mutations";
import { useWithholdingBalance } from "../../query/taxes/queries";
import { useAccounts } from "../../query/users/queries";
import { getPayoutAccount } from "../../query/users/selectors";
import { WSQueries } from "../../query/WSQuery";
import { CurrencyUSD } from "../../services/bookkeeping";
import { validatorAmount } from "../../utils/validators";

export const TaxesWithdraw: React.FC = () => {
  const { openSnackbar } = useWSSnackbar();
  const history = useHistory();
  const userId = useUserId();
  const payoutSettingsQuery = usePayoutSettings(userId);
  const qWithholdingBalance = useWithholdingBalance();
  const qAccounts = useAccounts();

  const [
    createPendingWithholding,
    createPendingWithholdingMeta
  ] = useCreatePendingWithholding();

  useBrowserPageTitle("Taxes - Withdraw from withholdings", "");

  return (
    <WSQueries
      queries={{
        qWithholdingBalance,
        qAccounts,
        payoutSettingsQuery
      }}
      renderLoader={() => (
        <WSLayout
          headerLeft={
            <WSIcon
              name="chevronLeft"
              size="M"
              color="grey600"
              onClick={() => history.goBack()}
            />
          }
          line
        />
      )}
    >
      {({
        qWithholdingBalance: { data: balance },
        qAccounts: { data: accounts },
        payoutSettingsQuery: { data: payoutSettings }
      }) => {
        const account = getPayoutAccount(payoutSettings, accounts);
        const withholdingBalance = Math.abs(balance.taxWithholdingBalance);

        return (
          <WSForm
            onSubmit={async formData => {
              await createPendingWithholding(
                {
                  amount: formData.amount,
                  type: WithholdingTransactionType.TaxWithholding,
                  subType: PendingWithholdingSubType.Withdraw,
                  currency: CurrencyUSD
                },
                {
                  throwOnError: true
                }
              );

              openSnackbar({
                message: "Successfully sent!",
                duration: 3000,
                type: "success"
              });

              history.push("/member/taxes/dashboard");
            }}
            defaultValues={{
              amount: (undefined as any) as number
            }}
            validationSchema={Yup.object().shape({
              amount: validatorAmount(withholdingBalance)
            })}
          >
            <WSLayout
              headerLeft={
                <WSIcon
                  name="chevronLeft"
                  size="M"
                  color="grey600"
                  onClick={() => history.goBack()}
                />
              }
              headerCenter={
                <WSText.Heading4>Withdraw from withholdings</WSText.Heading4>
              }
              line
            >
              <WSContainer verticalPadding>
                <WSText.Paragraph mt="S" mb="L" align="center">
                  Withdrawals will be sent to
                  {account?.mask && account.institution ? (
                    <b>{` ${account.institution} ${account.subType} (${account?.mask}) `}</b>
                  ) : (
                    " your bank account "
                  )}
                  and should appear in 1-2 business days.{" "}
                  <WSButton.Link
                    type="button"
                    inline
                    onClick={() =>
                      history.push("/member/settings/payment-methods")
                    }
                  >
                    Update direct deposit.
                  </WSButton.Link>
                </WSText.Paragraph>

                <WSGrid.Centered XS={6} S={8} M={6} L={6} XL={4}>
                  <WSForm.Field
                    name="amount"
                    mb="M"
                    component={WSAmountInput}
                  />
                </WSGrid.Centered>

                <WSText.Label align="center" color="grey600">
                  {formatMoney(balance.taxWithholdingBalance * -1)} available
                </WSText.Label>

                <WSGrid.Centered XS={6} S={8} M={6} L={6} XL={4}>
                  <WSErrorMessage
                    mb="M"
                    error={createPendingWithholdingMeta.error}
                    contextKey="PendingWithholding"
                  />

                  <WSButton.Primary
                    fullWidth
                    mt="XL"
                    type="submit"
                    data-testid="submitWithdraw"
                  >
                    Send request
                  </WSButton.Primary>
                  <WSButton.Tertiary
                    fullWidth
                    mt="S"
                    mb="XL"
                    type="button"
                    onClick={() => history.goBack()}
                  >
                    Cancel
                  </WSButton.Tertiary>
                </WSGrid.Centered>
              </WSContainer>
            </WSLayout>
          </WSForm>
        );
      }}
    </WSQueries>
  );
};
